// Small, local design-token layer for the Vendor Catalog screens.
// Intentionally scoped to this feature (not a global theme change) so it can
// serve as a low-risk preview of a more modern visual language before it's
// rolled out anywhere else in the app.

import { format } from "date-fns";

// Uploader roster for the "uploaded by" filter and the per-uploader KPIs. Display only:
// who may upload/edit/delete is governed by the VENDOR_CATALOG_MANAGE permission.
export const SCM_ADMIN_EMAILS = [
  "Sivakrishna.Adapala@eyantra.com",
  "Mohammad.Ubaid@eyantra.com",
  "Rahul.Rasa@eyantra.com",
  "Tech@eyantra.com",
  "Satheesh.Alapati@eyantra.com",
  "Sanjeev.Bandari@eyantra.com",
  "Srikanth.Kummari@eyantra.com",
  "AbdulRaheem.khan@eyantra.com",
  "Khem.Singh@eyantra.com",
  "Kusum.Ketan@eyantra.com",
  "Manik.Pathak@eyantra.com",
  "Rajkumar.Singh@eyantra.com",
  "Aravind.Naga@eyantra.com",
  "Maheswari.Kanike@eyantra.com"
];

export const catalogTokens = {
  radius: { sm: 8, md: 12, lg: 16, xl: 20 },
  shadow: {
    sm: "0 1px 2px rgba(15,23,42,0.06)",
    md: "0 6px 16px rgba(15,23,42,0.08)",
    lg: "0 16px 40px rgba(15,23,42,0.14)",
  },
  color: {
    surface: "#ffffff",
    surfaceMuted: "#f8fafc",
    border: "#e2e8f0",
    textPrimary: "#0f172a",
    textSecondary: "#64748b",
    primary: "#2563eb",
    primaryDark: "#1d4ed8",
    primarySoft: "#eff6ff",
  },
};

// Soft/foreground color pairs used to color-code chips (category, brand, ...) consistently
// (same name always maps to the same pair) without a new dependency.
const TAG_PALETTE = [
  { bg: "#eff6ff", fg: "#2563eb" },
  { bg: "#f0fdf4", fg: "#16a34a" },
  { bg: "#fdf4ff", fg: "#a21caf" },
  { bg: "#fff7ed", fg: "#c2410c" },
  { bg: "#fef2f2", fg: "#dc2626" },
  { bg: "#f0fdfa", fg: "#0d9488" },
  { bg: "#fefce8", fg: "#a16207" },
];

export function colorForTag(name = "") {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return TAG_PALETTE[Math.abs(hash) % TAG_PALETTE.length];
}

export function formatPriceRange(min, max) {
  const fmt = (n) => `₹${Number(n).toLocaleString("en-IN")}`;
  return `${fmt(min)} – ${fmt(max)}`;
}

//Group catalogs by vendor and compute some aggregate stats for each vendor group. Used by VendorCatalogFinder and VendorCatalogSearch.
export function groupCatalogsByVendor(catalogs) {
  const byId = new Map();
  const order = [];

  const dedupeById = (list, keyOf) => {
    const seen = new Map();
    for (const item of list) {
      const key = keyOf(item);
      if (!seen.has(key)) seen.set(key, item);
    }
    return [...seen.values()];
  };

  for (const catalog of catalogs || []) {
    if (!byId.has(catalog.vendorId)) {
      byId.set(catalog.vendorId, []);
      order.push(catalog.vendorId);
    }
    byId.get(catalog.vendorId).push(catalog);
  }

  return order.map((vendorId) => {
    const vendorCatalogs = byId.get(vendorId);
    const latest = vendorCatalogs.reduce((a, b) => (!a || (b.uploadedAt && b.uploadedAt > a.uploadedAt) ? b : a), null);

    return {
      vendorId,
      vendorName: vendorCatalogs[0].vendorName,
      catalogs: vendorCatalogs,
      catalogCount: vendorCatalogs.length,
      categories: dedupeById(vendorCatalogs.flatMap((c) => c.categories || []), (c) => c.id),
      brands: dedupeById(vendorCatalogs.flatMap((c) => c.brands || []), (b) => b.id ?? b.name),
      subCategories: dedupeById(vendorCatalogs.flatMap((c) => c.subCategories || []), (sc) => sc.id ?? `${sc.categoryName}::${sc.name}`),
      collections: dedupeById(vendorCatalogs.flatMap((c) => c.collections || []), (col) => col.id),
      minPrice: vendorCatalogs.reduce((m, c) => (m == null || Number(c.minPrice) < Number(m) ? c.minPrice : m), null),
      maxPrice: vendorCatalogs.reduce((m, c) => (m == null || Number(c.maxPrice) > Number(m) ? c.maxPrice : m), null),
      hasSeparatePriceList: vendorCatalogs.some((c) => c.hasSeparatePriceList),
      latestUploadedAt: latest?.uploadedAt || null,
      latestUploadedBy: latest?.uploadedBy || null,
      latestRemarks: latest?.remarks || null,
    };
  });
}

export function formatMonthYear(year, month) {
  return format(new Date(year, month - 1, 1), "MMM yyyy");
}

//Helper to build query params for vendor catalog search API from the filters and selected month
export function buildVendorCatalogFilterParams(filters = {}, selectedMonth = null) {
  return {
    ...(filters.categoryNames?.length && { categoryName: filters.categoryNames.join(",") }),
    ...(filters.brandNames?.length && { brandName: filters.brandNames.join(",") }),
    ...(filters.subCategoryNames?.length && { subCategoryName: filters.subCategoryNames.join(",") }),
    ...(filters.occasionIds?.length && { occasionId: filters.occasionIds.join(",") }),
    ...(filters.celebrationIds?.length && { celebrationId: filters.celebrationIds.join(",") }),
    ...(filters.minPrice && { minPrice: filters.minPrice }),
    ...(filters.maxPrice && { maxPrice: filters.maxPrice }),
    ...(filters.uploadedBy && { uploadedBy: filters.uploadedBy }),
    ...(selectedMonth && { year: selectedMonth.year, month: selectedMonth.month }),
  };
}

//Vendor catalot timestamp parsing
export function parseServerTimestamp(value) {
  if (!value) return null;
  return new Date(/[Zz]|[+-]\d\d:\d\d$/.test(value) ? value : `${value}Z`);
}

//Uploaded by changed from email to name, but the backend still returns the email. This helper formats it for display.
export function formatUploaderName(email) {
  if (!email) return "Unknown";
  const localPart = email.split("@")[0];
  return localPart
    .split(".")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(" ");
}

// The backend returns short plain-text reasons for its own errors (e.g. "Vendor not
// found with id 5") which are safe and useful to show as-is. Anything else — HTML
// gateway error pages (502/504 from nginx), long bodies, stack traces — is not, so
// those fall back to a generic message instead of being dumped into the UI.
export async function parseApiError(res, fallback = "Something went wrong. Please try again.") {
  const contentType = res.headers.get("content-type") || "";
  if (contentType.includes("application/json")) {
    try {
      const data = await res.json();
      return typeof data === "string" ? data : data.message || fallback;
    } catch {
      return fallback;
    }
  }
  const text = (await res.text()).trim();
  if (text && text.length < 300 && !text.startsWith("<")) {
    return text;
  }
  return `${fallback} (HTTP ${res.status})`;
}
